import * as Sentry from '@sentry/nextjs'
import axios from 'axios'
import { type ReactNode, createContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import { GEO_IP_COUNTRY_COOKIE_NAME } from '@/lib/utils/cookies'

type GeoContextType = {
  country: string
}

type Props = {
  children: ReactNode
}

export const GeoContext = createContext<GeoContextType>({ country: '' })

export const GeoContextProvider = (props: Props) => {
  const [cookies, setCookie] = useCookies([GEO_IP_COUNTRY_COOKIE_NAME])
  const [countryCode, setCountryCode] = useState<string>(cookies[GEO_IP_COUNTRY_COOKIE_NAME] ?? '')

  const getCountryCode = async (remainingAttempts: number) => {
    // If we already have a country (from cookie or a previous lookup), do nothing
    if (countryCode) {
      return
    }

    try {
      const result = await axios('https://getipcountry.zoe.net')
      setCountryCode(result.data)
    } catch (caught) {
      if (remainingAttempts > 0) {
        return await getCountryCode(remainingAttempts - 1)
      }

      const message = caught && typeof caught === 'object' && 'message' in caught ? caught.message : undefined
      if (message === 'Network Error') {
        Sentry.captureException(new Error('GEO-IP lookup: Call to getipcountry.zoe.net failed with Network Error'))
      } else if (message === 'Request failed with status code 403') {
        Sentry.captureException(new Error('GEO-IP lookup: Call to getipcountry.zoe.net failed with 403'))
      } else {
        throw caught
      }
    }
  }

  // When we learn a country (from cookie or API), make sure it is persisted in the cookie
  useEffect(() => {
    if (countryCode && cookies[GEO_IP_COUNTRY_COOKIE_NAME] !== countryCode) {
      setCookie(GEO_IP_COUNTRY_COOKIE_NAME, countryCode, {
        path: '/',
        maxAge: 60 * 60 * 24 * 30, // 30 days
        sameSite: 'lax',
      })
    }
  }, [cookies, countryCode, setCookie])

  // If middleware has already set the cookie, prefer that and sync state from it
  useEffect(() => {
    const cookieCountry = cookies[GEO_IP_COUNTRY_COOKIE_NAME]
    if (cookieCountry && !countryCode) {
      setCountryCode(cookieCountry)
    }
  }, [cookies, cookies[GEO_IP_COUNTRY_COOKIE_NAME], countryCode])

  // Fallback: if there is still no cookie, use the external service as backup
  useEffect(() => {
    if (!cookies[GEO_IP_COUNTRY_COOKIE_NAME]) {
      void getCountryCode(5)
    }
  }, [cookies, cookies[GEO_IP_COUNTRY_COOKIE_NAME]])

  return <GeoContext.Provider value={{ country: countryCode }}>{props.children}</GeoContext.Provider>
}
